/**
 * Menu + Testimonial editor modals.
 * Extracted from AdminDashboard.jsx (was ~215 inline lines).
 */
import React, { useState } from "react";
import { Upload } from "lucide-react";
import api from "../../api";
import { toast } from "sonner";
import { Field, TextArea, Modal } from "./AdminFormPrimitives";

export const MenuEditorModal = ({ item, onClose, onSave }) => {
    const [form, setForm] = useState(item);
    const [uploading, setUploading] = useState(false);
    const update = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
    const handle = (e) => {
        e.preventDefault();
        onSave({
            ...form,
            slug:
                form.slug ||
                form.name_es.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
        });
    };
    const pickAndUpload = () => {
        const inp = document.createElement("input");
        inp.type = "file";
        inp.accept = "image/*";
        inp.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 6 * 1024 * 1024) {
                toast.error("Imagen demasiado grande (máx 6 MB)");
                return;
            }
            setUploading(true);
            try {
                const fd = new FormData();
                fd.append("file", file);
                if (form.slug) fd.append("slug", form.slug);
                const r = await api.post("/admin/menu/upload-image", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                update("image_url")(r.data.image_url);
                toast.success("Foto subida");
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Error al subir");
            } finally {
                setUploading(false);
            }
        };
        inp.click();
    };
    return (
        <Modal title={item.id ? "Editar plato" : "Nuevo plato"} onClose={onClose}>
            <form onSubmit={handle} className="space-y-4" data-testid="menu-editor-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nombre (ES)" value={form.name_es} onChange={update("name_es")} required testId="menu-name-es" />
                    <Field label="Nombre (EN)" value={form.name_en} onChange={update("name_en")} required testId="menu-name-en" />
                </div>
                <TextArea label="Descripción (ES)" value={form.description_es} onChange={update("description_es")} testId="menu-desc-es" />
                <TextArea label="Descripción (EN)" value={form.description_en} onChange={update("description_en")} testId="menu-desc-en" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Precio (USD)" type="number" value={form.price} onChange={update("price")} required testId="menu-price" />
                    <Field label="Categoría" value={form.category} onChange={update("category")} testId="menu-category" />
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                        Foto del plato<span className="text-terracotta-500"> *</span>
                    </label>
                    <div className="flex items-center gap-3">
                        <div className="w-20 h-20 rounded-xl bg-earth-100 border border-earth-200 overflow-hidden shrink-0 flex items-center justify-center">
                            {form.image_url ? (
                                <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-earth-400 text-[10px] uppercase tracking-wider">sin foto</span>
                            )}
                        </div>
                        <div className="flex-1 space-y-2">
                            <button
                                type="button"
                                data-testid="menu-upload-image-btn"
                                onClick={pickAndUpload}
                                disabled={uploading}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-earth-900 hover:bg-terracotta-500 text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                {uploading ? "Subiendo…" : (form.image_url ? "Reemplazar foto" : "Subir foto")}
                            </button>
                            <input
                                data-testid="menu-image-url"
                                value={form.image_url}
                                onChange={(e) => update("image_url")(e.target.value)}
                                placeholder="o pegar URL manualmente"
                                className="w-full bg-earth-50 border border-earth-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-terracotta-500"
                                required
                            />
                        </div>
                    </div>
                    <p className="text-[10px] text-earth-500 mt-2">JPG / PNG / WEBP / HEIC · máx 6 MB</p>
                </div>
                <Field label="Slug (URL)" value={form.slug} onChange={update("slug")} testId="menu-slug" />
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Porción (ES)" value={form.serving_size_es} onChange={update("serving_size_es")} testId="menu-serving-es" />
                    <Field label="Serving (EN)" value={form.serving_size_en} onChange={update("serving_size_en")} testId="menu-serving-en" />
                    <Field
                        label="Cantidad estándar"
                        type="number"
                        value={form.default_quantity}
                        onChange={(v) => update("default_quantity")(Math.max(1, parseInt(v) || 1))}
                        testId="menu-default-qty"
                    />
                </div>
                <div className="flex flex-wrap gap-6">
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-earth-700">
                        <input type="checkbox" checked={form.featured} onChange={(e) => update("featured")(e.target.checked)} className="rounded" />
                        Destacado
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-earth-700">
                        <input type="checkbox" checked={form.available} onChange={(e) => update("available")(e.target.checked)} className="rounded" />
                        Disponible
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-earth-700">
                        <input
                            type="checkbox"
                            checked={form.preorder_enabled !== false}
                            onChange={(e) => update("preorder_enabled")(e.target.checked)}
                            className="rounded"
                            data-testid="menu-preorder-enabled"
                        />
                        Pre-orden 72h habilitada
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-earth-700">
                        <input
                            type="checkbox"
                            checked={form.catering_only === true}
                            onChange={(e) => update("catering_only")(e.target.checked)}
                            className="rounded"
                            data-testid="menu-catering-only"
                        />
                        Solo para catering
                    </label>
                </div>
                <div className="flex gap-3 pt-3">
                    <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
                    <button type="submit" className="btn-primary flex-1" data-testid="menu-save-btn">Guardar</button>
                </div>
            </form>
        </Modal>
    );
};

export const TestimonialEditorModal = ({ item, onClose, onSave }) => {
    const [form, setForm] = useState(item);
    const update = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
    const handle = (e) => {
        e.preventDefault();
        onSave(form);
    };
    return (
        <Modal title="Nuevo testimonio" onClose={onClose}>
            <form onSubmit={handle} className="space-y-4" data-testid="testimonial-editor-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Autor" value={form.author} onChange={update("author")} required testId="testi-author" />
                    <Field label="Ubicación" value={form.location} onChange={update("location")} testId="testi-location" />
                </div>
                <Field label="Calificación (1-5)" type="number" value={form.rating} onChange={update("rating")} testId="testi-rating" />
                <TextArea label="Texto (ES)" value={form.text_es} onChange={update("text_es")} testId="testi-text-es" />
                <TextArea label="Texto (EN)" value={form.text_en} onChange={update("text_en")} testId="testi-text-en" />
                <div className="flex gap-3 pt-3">
                    <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
                    <button type="submit" className="btn-primary flex-1" data-testid="testi-save-btn">Guardar</button>
                </div>
            </form>
        </Modal>
    );
};
